import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Eye, EyeOff, Gift, Mail, CheckCircle2 } from 'lucide-react';
import Logo from './Logo';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_OPTIONS = {
  starter: {
    id: 'starter',
    name: 'Starter',
    price: '79 EUR lifetime',
    detail: '1 sito WordPress · 300 crediti inclusi'
  },
  professional: {
    id: 'professional',
    name: 'Professional',
    price: '129 EUR lifetime',
    detail: '3 siti WordPress · 500 crediti inclusi'
  },
  team: {
    id: 'team',
    name: 'Team',
    price: '259 EUR lifetime',
    detail: '10 siti WordPress · 1000 crediti inclusi'
  }
};

const normalizePlanId = (value) => {
  const normalized = (value || '').trim().toLowerCase();
  return PLAN_OPTIONS[normalized] ? normalized : '';
};

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const selectedPlanId = normalizePlanId(searchParams.get('plan'));
  const selectedPlan = selectedPlanId ? PLAN_OPTIONS[selectedPlanId] : null;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [referralValid, setReferralValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent
  const [resendMsg, setResendMsg] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleResend = async () => {
    setResendState('sending');
    setResendMsg('');
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, { email });
      setResendState('sent');
      setResendMsg(`Ti abbiamo rimandato il link di verifica a ${email}.`);
    } catch (err) {
      setResendState('idle');
      setResendMsg(err.response?.data?.detail || 'Invio non riuscito. Riprova tra poco.');
    }
  };

  useEffect(() => {
    if (referralCode) {
      validateReferralCode(referralCode);
    }
  }, []);

  useEffect(() => {
    if (!selectedPlanId) return;
    try {
      localStorage.setItem('aeo_pending_plan', selectedPlanId);
      sessionStorage.setItem('aeo_pending_plan', selectedPlanId);
    } catch {
      // Storage can be blocked in hardened browsers; the visible plan remains authoritative.
    }
  }, [selectedPlanId]);

  const validateReferralCode = async (code) => {
    try {
      const response = await axios.get(`${API_URL}/api/referral/validate/${code}`);
      setReferralValid(response.data.valid);
    } catch {
      setReferralValid(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (password.length < 8) {
      setError('La password deve essere di almeno 8 caratteri');
      setLoading(false);
      return;
    }

    if (!/\d/.test(password)) {
      setError('La password deve contenere almeno 1 numero');
      setLoading(false);
      return;
    }

    try {
      await register(email, password, companyName, referralCode, selectedPlanId);
      setRegistered(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registrazione fallita. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  if (registered) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-24" data-testid="register-success-page">
        <div className="w-full max-w-md text-center">
          <Link to="/" className="flex items-center justify-center gap-3 mb-8">
            <Logo size={48} className="mx-auto mb-3" />
            <span className="font-['Outfit'] font-semibold text-2xl tracking-tight">
              AEO Orchestra <span className="text-[#0055FF]">AI</span>
            </span>
          </Link>
          <div className="bg-[#111111] border border-white/10 rounded-3xl p-8">
            <div className="w-16 h-16 rounded-full bg-[#0055FF]/10 flex items-center justify-center mx-auto mb-5">
              <Mail className="w-8 h-8 text-[#0055FF]" />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-medium mb-3">Controlla la tua email</h1>
            <p className="text-[#A1A1AA] text-sm mb-2">
              Abbiamo inviato un link di verifica a:
            </p>
            <p className="text-white font-medium mb-6">{email}</p>
            <div className="bg-[#0a0a0a] rounded-xl p-4 mb-6 text-left space-y-2">
              <div className="flex items-start gap-2 text-sm text-[#A1A1AA]">
                <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                <span>Account creato con <span className="text-white">50 crediti trial</span></span>
              </div>
              <div className="flex items-start gap-2 text-sm text-[#A1A1AA]">
                <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                <span>Prova gratuita <span className="text-white">7 giorni</span> attivata</span>
              </div>
              {selectedPlan && (
                <div className="flex items-start gap-2 text-sm text-[#A1A1AA]" data-testid="register-selected-plan-success">
                  <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                  <span>Piano scelto: <span className="text-white">{selectedPlan.name}</span></span>
                </div>
              )}
              <div className="flex items-start gap-2 text-sm text-[#A1A1AA]">
                <Mail className="w-4 h-4 text-[#0055FF] mt-0.5 flex-shrink-0" />
                <span>Verifica l'email per accedere alla dashboard</span>
              </div>
            </div>
            <Link to="/login">
              <Button className="w-full bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full py-6 font-medium" data-testid="go-to-login-btn">
                Vai al Login
              </Button>
            </Link>
            <p className="text-xs text-[#A1A1AA] mt-4">
              Non hai ricevuto l'email? Controlla la cartella spam
              {resendState === 'sent' ? '.' : (
                <>
                  {' '}oppure{' '}
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendState === 'sending'}
                    className="text-[#0055FF] hover:text-[#3377FF] font-medium disabled:opacity-50"
                    data-testid="register-resend-btn"
                  >
                    {resendState === 'sending' ? 'invio in corso…' : 'invia di nuovo il link'}
                  </button>.
                </>
              )}
            </p>
            {resendMsg && (
              <p className={`text-xs mt-2 ${resendState === 'sent' ? 'text-[#10B981]' : 'text-red-400'}`} data-testid="register-resend-msg">
                {resendMsg}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24" data-testid="register-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <Logo size={48} className="mx-auto mb-3" />
          <span className="font-['Outfit'] font-semibold text-2xl tracking-tight">
            AEO Orchestra <span className="text-[#0055FF]">AI</span>
          </span>
        </Link>

        {/* Card */}
        <div className="bg-[#111111] border border-white/10 rounded-3xl p-8">
          <h1 className="font-['Outfit'] text-2xl font-medium text-center mb-2">Crea il tuo account</h1>
          <p className="text-[#A1A1AA] text-center mb-8">
            {selectedPlan ? `Completa la registrazione per continuare con ${selectedPlan.name}` : 'Inizia a usare AEO Orchestra oggi'}
          </p>

          {selectedPlan && (
            <div
              className="mb-6 rounded-2xl border border-[#0055FF]/30 bg-[#0055FF]/10 p-4"
              data-testid="register-selected-plan"
              data-selected-plan={selectedPlan.id}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[#7AA2FF]">
                    Piano selezionato
                  </span>
                  <strong className="mt-1 block font-['Outfit'] text-xl font-semibold text-white">
                    {selectedPlan.name}
                  </strong>
                  <span className="mt-1 block text-sm text-[#A1A1AA]">{selectedPlan.detail}</span>
                </div>
                <span className="shrink-0 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white">
                  {selectedPlan.price}
                </span>
              </div>
              <Link to="/pricing" className="mt-3 inline-flex text-xs font-medium text-[#7AA2FF] hover:text-white">
                Cambia piano
              </Link>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="company" className="text-sm font-medium mb-2 block">Nome Azienda (opzionale)</Label>
              <Input
                id="company"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="La tua azienda"
                className="input-field"
                data-testid="register-company-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@azienda.com"
                className="input-field"
                required
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimo 8 caratteri"
                  className="input-field pr-12"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Referral Code Field */}
            <div>
              <Label htmlFor="referral" className="text-sm font-medium mb-2 block">
                Codice Referral (opzionale)
              </Label>
              <div className="relative">
                <Input
                  id="referral"
                  type="text"
                  value={referralCode}
                  onChange={(e) => {
                    setReferralCode(e.target.value);
                    if (e.target.value.length > 5) validateReferralCode(e.target.value);
                  }}
                  placeholder="REF-XXXX"
                  className="input-field"
                  data-testid="register-referral-input"
                />
                {referralValid && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2 text-[#10B981]">
                    <Gift className="w-4 h-4" />
                    <span className="text-xs">-20%</span>
                  </div>
                )}
              </div>
              {referralValid && (
                <p className="text-xs text-[#10B981] mt-1">
                  Codice valido! Riceverai 20% di sconto sul primo acquisto
                </p>
              )}
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full py-6 font-medium"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creazione account...' : 'Crea Account'}
            </Button>
          </form>

          <p className="mt-6 text-center text-[#A1A1AA] text-sm">
            Hai già un account?{' '}
            <Link to="/login" className="text-[#0055FF] hover:text-[#3377FF] font-medium">
              Accedi
            </Link>
          </p>

          <p className="mt-4 text-center text-[#A1A1AA] text-xs">
            Creando un account accetti i nostri{' '}
            <Link to="/terms" className="text-[#0055FF]">Termini di Servizio</Link>
            {' '}e la{' '}
            <Link to="/privacy" className="text-[#0055FF]">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
